<template>
  <div class="fb-page">
    <el-card shadow="never" class="header-card">
      <div class="page-header">
        <div>
          <h2>准备素材</h2>
          <p>先选择素材所属的 Account，再按素材类型进行批量上传。列表默认展示全量数据，包含该账号下已导入的内容。</p>
        </div>
      </div>

      <div class="account-section">
        <div class="account-block">
          <div class="section-label">素材所属 Account</div>
          <div class="account-cards" v-loading="accountLoading">
            <div
              v-for="item in accounts"
              :key="item.accountId"
              class="account-card"
              :class="{ active: selectedAccountId === item.accountId }"
              @click="selectedAccountId = item.accountId"
            >
              <span class="account-radio" :class="{ checked: selectedAccountId === item.accountId }" />
              <div class="account-meta">
                <div class="account-id">{{ displayAccountId(item.accountId) }}</div>
                <div class="account-site">关联域名：{{ item.webSiteUrl || '未配置站点' }}</div>
              </div>
            </div>
            <div v-if="!accountLoading && !accounts.length" class="empty-accounts">
              暂无可用 Account
            </div>
          </div>
        </div>

        <div class="header-actions">
          <el-button size="small" icon="el-icon-picture" @click="openImageUpload">
            批量上传广告图片
          </el-button>
          <el-button type="primary" size="small" icon="el-icon-video-camera" @click="openVideoUpload">
            批量上传广告视频
          </el-button>
        </div>
      </div>

      <el-alert
        class="upload-tip"
        type="info"
        show-icon
        :closable="false"
        title="广告视频和广告图片均支持一次选择多个文件批量上传，并使用独立入口区分素材类型。重复名称不允许上传。"
      />
    </el-card>

    <el-card shadow="never" class="table-card">
      <div class="table-toolbar">
        <div class="toolbar-left">
          <span class="table-title">素材列表</span>
          <el-select
            v-model="filterAccountId"
            size="small"
            clearable
            placeholder="全部 Account"
            style="width: 280px"
          >
            <el-option label="全部 Account" value="" />
            <el-option
              v-for="item in accounts"
              :key="item.accountId"
              :label="accountOptionLabel(item)"
              :value="item.accountId"
            />
          </el-select>
          <el-button size="small" icon="el-icon-search" @click="handleFilter">筛选</el-button>
          <span class="total-text">共 {{ total }} 条</span>
        </div>
        <div class="toolbar-right">
          <el-button size="small" icon="el-icon-refresh" :loading="refreshLoading" @click="refreshStatus">
            刷新状态
          </el-button>
          <el-button size="small" icon="el-icon-download" :loading="exportLoading" @click="handleExport">
            导出 Excel
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="tableLoading"
        :data="tableData"
        border
        stripe
        size="small"
        style="width: 100%"
      >
        <el-table-column prop="assetId" label="素材 ID" min-width="150" show-overflow-tooltip />
        <el-table-column label="Account ID" width="160" align="center">
          <template slot-scope="scope">
            {{ displayAccountId(scope.row.accountId) }}
          </template>
        </el-table-column>
        <el-table-column prop="assetType" label="素材类型" width="110" align="center">
          <template slot-scope="scope">
            {{ mapAssetTypeLabel(scope.row.assetType) }}
          </template>
        </el-table-column>
        <el-table-column prop="assetName" label="素材名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="campaignName" label="广告系列名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.campaignName || '未关联' }}
          </template>
        </el-table-column>
        <el-table-column label="上传状态" width="120" align="center">
          <template slot-scope="scope">
            <span class="status-pill" :class="'is-' + mapUploadStatus(scope.row.uploadStatus).type">
              {{ mapUploadStatus(scope.row.uploadStatus).label }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="uploadTime" label="上传时间" width="170" align="center" />
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </el-card>

    <el-dialog
      title="批量上传广告视频"
      :visible.sync="videoDialogVisible"
      width="560px"
      @closed="resetVideoDialog"
    >
      <el-form label-width="96px">
        <el-form-item label="Account">
          <span>{{ selectedAccountText }}</span>
        </el-form-item>
        <el-form-item label="视频文件">
          <el-upload
            ref="videoUpload"
            action="#"
            :auto-upload="false"
            multiple
            accept=".mp4,.mov,.m4v,.mpeg,video/*"
            :file-list="videoFileList"
            :on-change="handleVideoChange"
            :on-remove="handleVideoRemove"
            :before-upload="() => false"
          >
            <el-button size="small" type="primary">选择视频</el-button>
            <div slot="tip" class="el-upload__tip">支持批量选择，视频名称不可与已有素材重复</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="videoDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="videoUploading" @click="submitVideoUpload">开始上传</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="批量上传广告图片"
      :visible.sync="imageDialogVisible"
      width="560px"
      @closed="resetImageDialog"
    >
      <el-form label-width="96px">
        <el-form-item label="Account">
          <span>{{ selectedAccountText }}</span>
        </el-form-item>
        <el-form-item label="图片文件">
          <el-upload
            ref="imageUpload"
            action="#"
            :auto-upload="false"
            multiple
            accept=".png,.jpg,.jpeg,.gif,.webp,image/*"
            :file-list="imageFileList"
            :on-change="handleImageChange"
            :on-remove="handleImageRemove"
            :before-upload="() => false"
          >
            <el-button size="small" type="primary">选择图片</el-button>
            <div slot="tip" class="el-upload__tip">支持 JPG / PNG / GIF / WEBP，可一次选择多个文件</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="imageDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="imageUploading" @click="submitImageUpload">开始上传</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
  import {
    ASSET_TYPE,
    accountOptionLabel,
    displayAccountId,
    exportAssets,
    isAssetPending,
    listAccounts,
    listAssets,
    mapAssetTypeLabel,
    mapUploadStatus,
    uploadAndSubmitAssets
  } from '@/api/facebookAds'
  import { downloadExcel } from '@/utils/excel'

  export default {
    name: 'FBCreateAsset',
    data() {
      return {
        accounts: [],
        accountLoading: false,
        selectedAccountId: '',
        filterAccountId: '',
        appliedFilterAccountId: '',
        tableData: [],
        tableLoading: false,
        currentPage: 1,
        pageSize: 20,
        total: 0,
        refreshLoading: false,
        exportLoading: false,
        videoDialogVisible: false,
        imageDialogVisible: false,
        videoFileList: [],
        imageFileList: [],
        videoUploading: false,
        imageUploading: false,
        pollTimer: null
      }
    },
    computed: {
      selectedAccountText() {
        const item = this.accounts.find((account) => account.accountId === this.selectedAccountId)
        return item ? accountOptionLabel(item) : this.selectedAccountId
      }
    },
    mounted() {
      this.initPage()
    },
    beforeDestroy() {
      this.clearPoll()
    },
    methods: {
      accountOptionLabel,
      displayAccountId,
      mapAssetTypeLabel,
      mapUploadStatus,
      ensureAccountSelected() {
        if (this.selectedAccountId) return true
        this.$message.warning('请先选择所属 Account ID')
        return false
      },
      async initPage() {
        await this.loadAccounts()
        await this.loadData()
      },
      async loadAccounts() {
        this.accountLoading = true
        try {
          const response = await listAccounts()
          this.accounts = response.body.data || []
        } catch (error) {
          this.$message.error(error.message || '加载 Account 失败')
        } finally {
          this.accountLoading = false
        }
      },
      async loadData(silent = false) {
        if (!silent) this.tableLoading = true
        try {
          const response = await listAssets({
            accountId: this.appliedFilterAccountId,
            pageNum: this.currentPage,
            pageSize: this.pageSize
          })
          this.tableData = Array.isArray(response.body && response.body.data) ? response.body.data : []
          this.total = (response.body && response.body.total) || 0
          this.setupPoll()
        } catch (error) {
          this.$message.error(error.message || '加载素材失败')
        } finally {
          if (!silent) this.tableLoading = false
        }
      },
      handleFilter() {
        this.appliedFilterAccountId = this.filterAccountId
        this.currentPage = 1
        this.loadData()
      },
      async refreshStatus() {
        this.refreshLoading = true
        try {
          await this.loadData()
          this.$message.success('状态已刷新')
        } finally {
          this.refreshLoading = false
        }
      },
      openVideoUpload() {
        if (!this.ensureAccountSelected()) return
        this.videoDialogVisible = true
      },
      openImageUpload() {
        if (!this.ensureAccountSelected()) return
        this.imageDialogVisible = true
      },
      resetVideoDialog() {
        this.videoFileList = []
        if (this.$refs.videoUpload) this.$refs.videoUpload.clearFiles()
      },
      resetImageDialog() {
        this.imageFileList = []
        if (this.$refs.imageUpload) this.$refs.imageUpload.clearFiles()
      },
      handleVideoChange(file, fileList) {
        this.videoFileList = fileList
      },
      handleVideoRemove(file, fileList) {
        this.videoFileList = fileList
      },
      handleImageChange(file, fileList) {
        this.imageFileList = fileList
      },
      handleImageRemove(file, fileList) {
        this.imageFileList = fileList
      },
      getRawFiles(fileList) {
        return fileList.map((item) => item.raw).filter(Boolean)
      },
      validateAssetFiles(files) {
        const oversized = files.filter((file) => file.size > 200 * 1024 * 1024)
        if (oversized.length) {
          this.$message.error(`单个文件最大 200MB：${oversized.map((file) => file.name).join('、')}`)
          return false
        }
        return true
      },
      fileBaseName(name) {
        return String(name || '').replace(/\.[^.]+$/, '').toLowerCase()
      },
      async findDuplicateNames(files, assetType) {
        const selectedNames = files.map((file) => file.name)
        const selectedDup = selectedNames.filter(
          (name, index) =>
            selectedNames.findIndex((item) => this.fileBaseName(item) === this.fileBaseName(name)) !== index
        )
        const response = await listAssets({
          accountId: this.selectedAccountId,
          pageNum: 1,
          pageSize: 10000
        })
        const existedDup = (Array.isArray(response.body && response.body.data) ? response.body.data : [])
          .filter((item) => {
            if (item.assetType !== assetType) return false
            const existingName = item.sourceFileName || item.assetName || ''
            return selectedNames.some(
              (name) =>
                this.fileBaseName(name) === this.fileBaseName(existingName) ||
                name.toLowerCase() === String(existingName).toLowerCase()
            )
          })
          .map((item) => item.sourceFileName || item.assetName)
        return Array.from(new Set(selectedDup.concat(existedDup)))
      },
      getSelectedAccount() {
        return this.accounts.find((account) => account.accountId === this.selectedAccountId) || {}
      },
      async submitVideoUpload() {
        const files = this.getRawFiles(this.videoFileList)
        if (!files.length) {
          this.$message.warning('请选择要上传的视频')
          return
        }
        if (!this.validateAssetFiles(files)) return
        const duplicates = await this.findDuplicateNames(files, ASSET_TYPE.VIDEO)
        if (duplicates.length) {
          this.$message.error(`视频名称重复，不允许上传：${duplicates.join('、')}`)
          return
        }
        this.videoUploading = true
        try {
          await uploadAndSubmitAssets({
            accountId: this.selectedAccountId,
            webSiteUrl: this.getSelectedAccount().webSiteUrl,
            assetType: ASSET_TYPE.VIDEO,
            files
          })
          this.$message.success('视频已提交上传')
          this.videoDialogVisible = false
          this.appliedFilterAccountId = this.selectedAccountId
          this.filterAccountId = this.selectedAccountId
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          this.$message.error(error.message || '视频上传失败')
        } finally {
          this.videoUploading = false
        }
      },
      async submitImageUpload() {
        const files = this.getRawFiles(this.imageFileList)
        if (!files.length) {
          this.$message.warning('请选择要上传的图片')
          return
        }
        if (!this.validateAssetFiles(files)) return
        const duplicates = await this.findDuplicateNames(files, ASSET_TYPE.IMAGE)
        if (duplicates.length) {
          this.$message.error(`图片名称重复，不允许上传：${duplicates.join('、')}`)
          return
        }
        this.imageUploading = true
        try {
          await uploadAndSubmitAssets({
            accountId: this.selectedAccountId,
            webSiteUrl: this.getSelectedAccount().webSiteUrl,
            assetType: ASSET_TYPE.IMAGE,
            files
          })
          this.$message.success('图片已提交上传')
          this.imageDialogVisible = false
          this.appliedFilterAccountId = this.selectedAccountId
          this.filterAccountId = this.selectedAccountId
          this.currentPage = 1
          await this.loadData()
        } catch (error) {
          this.$message.error(error.message || '图片上传失败')
        } finally {
          this.imageUploading = false
        }
      },
      async handleExport() {
        this.exportLoading = true
        try {
          const result = await exportAssets({
            accountId: this.appliedFilterAccountId,
            pageNum: 1,
            pageSize: 10000
          })
          if (result.mode === 'empty') {
            await this.exportCurrentList()
          }
          this.$message.success('导出成功')
        } catch (error) {
          try {
            await this.exportCurrentList()
            this.$message.success('导出成功')
          } catch (fallbackError) {
            this.$message.error(error.message || '导出失败')
          }
        } finally {
          this.exportLoading = false
        }
      },
      async exportCurrentList() {
        const response = await listAssets({
          accountId: this.appliedFilterAccountId,
          pageNum: 1,
          pageSize: 10000
        })
        const rows = (Array.isArray(response.body && response.body.data) ? response.body.data : []).map((item) => [
          item.assetId || item.id,
          item.accountId,
          item.webSiteUrl || '',
          this.mapAssetTypeLabel(item.assetType),
          item.sourceFileName || item.assetName || '',
          item.campaignName || '',
          this.mapUploadStatus(item.uploadStatus).label,
          item.uploadTime || item.createTime || ''
        ])
        downloadExcel('Facebook素材列表.xlsx', [
          {
            name: '素材列表',
            header: ['素材ID', '账号ID', '站点', '素材类型', '素材文件名', '广告系列', '上传状态', '创建时间'],
            rows
          }
        ])
      },
      handleSizeChange(val) {
        this.pageSize = val
        this.currentPage = 1
        this.loadData()
      },
      handleCurrentChange(val) {
        this.currentPage = val
        this.loadData()
      },
      setupPoll() {
        this.clearPoll()
        const hasPending = this.tableData.some((item) => isAssetPending(item.uploadStatus))
        if (!hasPending) return
        this.pollTimer = setInterval(() => {
          this.loadData(true)
        }, 4000)
      },
      clearPoll() {
        if (this.pollTimer) {
          clearInterval(this.pollTimer)
          this.pollTimer = null
        }
      }
    }
  }
</script>

<style scoped>
  .fb-page {
    min-height: calc(100vh - 60px);
    padding: 20px;
    background: #f5f7fa;
  }

  .header-card,
  .table-card {
    border-radius: 8px;
  }

  .page-header h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 600;
    color: #303133;
  }

  .page-header p {
    margin: 0;
    color: #909399;
    font-size: 13px;
    line-height: 1.6;
  }

  .account-section {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
  }

  .section-label {
    margin-bottom: 10px;
    font-size: 13px;
    color: #606266;
  }

  .account-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 64px;
  }

  .account-card {
    min-width: 220px;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border: 1px solid #dcdfe6;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .account-radio {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 3px;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    background: #fff;
    box-sizing: border-box;
  }

  .account-radio.checked {
    border-color: #409eff;
    background: #fff;
    box-shadow: inset 0 0 0 4px #409eff;
  }

  .account-card:hover,
  .account-card.active {
    border-color: #409eff;
    background: #ecf5ff;
  }

  .account-id {
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  .account-card.active .account-id,
  .account-card.active .account-site {
    color: #409eff;
  }

  .account-site {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
  }

  .empty-accounts {
    color: #909399;
    font-size: 13px;
    line-height: 64px;
  }

  .header-actions {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
  }

  .upload-tip {
    margin-top: 16px;
  }

  .table-card {
    margin-top: 16px;
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .table-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .total-text {
    color: #909399;
    font-size: 13px;
  }

  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  .status-pill {
    display: inline-block;
    min-width: 64px;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    border: 1px solid;
    line-height: 1.6;
  }

  .status-pill.is-success {
    color: #67c23a;
    background: #f0f9eb;
    border-color: #c2e7b0;
  }

  .status-pill.is-warning {
    color: #e6a23c;
    background: #fdf6ec;
    border-color: #f5dab1;
  }

  .status-pill.is-danger {
    color: #f56c6c;
    background: #fef0f0;
    border-color: #fbc4c4;
  }

  .status-pill.is-info {
    color: #909399;
    background: #f4f4f5;
    border-color: #d3d4d6;
  }

  @media (max-width: 768px) {
    .fb-page {
      padding: 12px;
    }

    .account-section {
      flex-direction: column;
    }

    .pagination-container {
      justify-content: center;
    }
  }
</style>
